import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Mic, Square, Pause, Play, Trash2, X, Upload, Loader2, CheckCircle2, AlertCircle, FileText, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAudioRecorder } from './useAudioRecorder'
import { uploadRecording, getRecording, regenerateRecordingSummary } from './api'
import { BottomSheet } from '@tendbot/ui'

interface Props {
  isOpen: boolean
  onClose: () => void
  onNavigateToNote?: (noteId: string) => void
  /** Notebook entry this recording is being made from (for automatic cross-linking) */
  linkedEntryId?: string | null
  linkedEntryTitle?: string | null
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

const WAVEFORM_BARS = 24

export default function RecordingSheet({ isOpen, onClose, onNavigateToNote, linkedEntryId, linkedEntryTitle }: Props) {
  const { t } = useTranslation('recording')
  const { state, elapsedSeconds, audioLevel, audioSegments, wasInterrupted, error, start, stop, pause, resume, reset } = useAudioRecorder()
  const totalSegmentBytes = audioSegments?.reduce((sum, s) => sum + s.size, 0) ?? 0
  const hasAudio = (audioSegments?.length ?? 0) > 0
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [transcriptionStatus, setTranscriptionStatus] = useState<'idle' | 'transcribing' | 'processing' | 'done' | 'failed'>('idle')
  const [notebookEntryId, setNotebookEntryId] = useState<string | null>(null)
  const [recordingId, setRecordingId] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const canClose = state !== 'recording' && state !== 'paused'

  const handleClose = () => {
    if (!canClose) return
    if (pollRef.current) clearInterval(pollRef.current)
    setTranscriptionStatus('idle')
    setNotebookEntryId(null)
    setRecordingId(null)
    setRetrying(false)
    reset()
    onClose()
  }

  const handleDiscard = () => {
    setShowDiscardConfirm(false)
    reset()
  }

  const pollTranscription = (id: string) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const recording = await getRecording(id)
        if (recording.status === 'processing') {
          setTranscriptionStatus('processing')
        } else if (recording.status === 'done' || recording.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = null
          setTranscriptionStatus(recording.status)
          if (recording.notebookEntryId) {
            setNotebookEntryId(recording.notebookEntryId)
          }
        }
      } catch {
        // Ignore polling errors — will retry next interval
      }
    }, 3000)
  }

  const handleSave = async () => {
    if (!audioSegments || audioSegments.length === 0 || uploading) return
    setUploading(true)
    setUploadError(null)
    try {
      const result = await uploadRecording(audioSegments, elapsedSeconds, linkedEntryId)
      setUploading(false)
      setRecordingId(result.id)
      setTranscriptionStatus('transcribing')
      pollTranscription(result.id)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
      setUploading(false)
    }
  }

  const handleRetry = async () => {
    if (!recordingId || retrying) return
    setRetrying(true)
    try {
      await regenerateRecordingSummary(recordingId)
      setTranscriptionStatus('transcribing')
      pollTranscription(recordingId)
    } catch {
      // Leave failed state visible so user can try again
    } finally {
      setRetrying(false)
    }
  }

  const handleViewNotebookEntry = () => {
    if (!notebookEntryId || !onNavigateToNote) return
    const id = notebookEntryId
    handleClose()
    onNavigateToNote(id)
  }

  return (
    <BottomSheet
      open={isOpen}
      onOpenChange={(o) => { if (!o) handleClose() }}
      title={linkedEntryTitle ? `${t('title')} — ${linkedEntryTitle}` : t('title')}
      swipeEnabled={canClose}
      className="pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex items-center justify-between px-5 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Mic size={18} className="text-gold shrink-0" />
          <h3 className="font-display text-ivory font-semibold truncate">
            {linkedEntryTitle ? `${t('title')} — ${linkedEntryTitle}` : t('title')}
          </h3>
        </div>
        <button
          onClick={handleClose}
          disabled={!canClose}
          className="w-8 h-8 rounded-full bg-slate-mid/30 flex items-center justify-center text-pearl/60 hover:text-ivory hover:bg-slate-mid/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-5 pb-6 flex flex-col items-center gap-6">
        <div className="flex items-center justify-center gap-1 h-20 w-full max-w-xs">
          {Array.from({ length: WAVEFORM_BARS }).map((_, i) => {
            const isActive = state === 'recording'
            const offset = Math.sin(i * 0.7) * 0.3 + 0.5
            const height = isActive
              ? Math.max(4, audioLevel * 80 * offset + 4)
              : state === 'paused' ? 8 : 4
            return (
              <motion.div
                key={i}
                className={`w-1.5 rounded-full ${
                  isActive ? 'bg-gold' :
                  state === 'paused' ? 'bg-gold/40' :
                  'bg-slate-mid/40'
                }`}
                animate={{ height }}
                transition={{ type: 'spring', stiffness: 300, damping: 20, mass: 0.5 }}
              />
            )
          })}
        </div>

        <div className="text-center">
          <p className="font-mono text-3xl text-ivory tracking-wider">{formatTime(elapsedSeconds)}</p>
          {state === 'recording' && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-rose text-xs mt-1.5 flex items-center justify-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-rose animate-pulse" />
              {t('recording')}
            </motion.p>
          )}
          {state === 'paused' && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-amber text-xs mt-1.5">
              {t('paused')}
            </motion.p>
          )}
        </div>

        <AnimatePresence>
          {(error || uploadError) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="glass-card rounded-xl p-3 border border-rose/20 text-center max-w-xs"
            >
              <p className="text-rose text-sm">{error ? t(`error.${error}`) : uploadError}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-center gap-6">
          {state === 'idle' && (
            <motion.button
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={start}
              className="w-18 h-18 rounded-full bg-gold text-midnight flex items-center justify-center shadow-lg shadow-gold/20 hover:bg-gold-light transition-colors"
            >
              <Mic size={28} />
            </motion.button>
          )}

          {(state === 'recording' || state === 'paused') && (
            <>
              <motion.button
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileTap={{ scale: 0.95 }}
                onClick={state === 'recording' ? pause : resume}
                className="w-12 h-12 rounded-full bg-slate-mid/50 border border-gold/20 text-ivory flex items-center justify-center hover:bg-slate-mid/70 transition-colors"
              >
                {state === 'recording' ? <Pause size={20} /> : <Play size={20} />}
              </motion.button>

              <motion.button
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileTap={{ scale: 0.95 }}
                onClick={stop}
                className="w-18 h-18 rounded-full bg-rose/20 border-2 border-rose/40 text-rose flex items-center justify-center hover:bg-rose/30 transition-colors"
              >
                <Square size={24} fill="currentColor" />
              </motion.button>
            </>
          )}

          {state === 'stopped' && hasAudio && transcriptionStatus === 'idle' && (
            <>
              <motion.button
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDiscardConfirm(true)}
                disabled={uploading}
                className="w-12 h-12 rounded-full bg-slate-mid/50 border border-rose/20 text-rose flex items-center justify-center hover:bg-rose/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Trash2 size={20} />
              </motion.button>

              <motion.button
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSave}
                disabled={uploading}
                className="w-18 h-18 rounded-full bg-gold text-midnight flex items-center justify-center shadow-lg shadow-gold/20 hover:bg-gold-light transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 size={28} className="animate-spin" /> : <Upload size={28} />}
              </motion.button>
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-6 text-xs text-pearl/60">
          {state === 'idle' && <span>{t('controls.start')}</span>}
          {(state === 'recording' || state === 'paused') && (
            <>
              <span>{state === 'recording' ? t('controls.pause') : t('controls.resume')}</span>
              <span>{t('controls.stop')}</span>
            </>
          )}
          {state === 'stopped' && hasAudio && transcriptionStatus === 'idle' && (
            <>
              <span>{t('controls.discard')}</span>
              <span>{uploading ? t('controls.uploading') : t('controls.upload')}</span>
            </>
          )}
        </div>

        {state === 'stopped' && hasAudio && transcriptionStatus === 'idle' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-xl p-3 text-center">
            <p className="text-ivory text-sm font-medium">{formatTime(elapsedSeconds)}</p>
            <p className="text-pearl/60 text-xs mt-0.5">
              {(totalSegmentBytes / (1024 * 1024)).toFixed(1)} MB
              {wasInterrupted && audioSegments && audioSegments.length > 1 && (
                <> · {t('interrupted', { count: audioSegments.length })}</>
              )}
            </p>
          </motion.div>
        )}

        <AnimatePresence>
          {transcriptionStatus === 'transcribing' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="glass-card rounded-xl p-4 text-center border border-gold/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Loader2 size={16} className="text-gold animate-spin" />
                <p className="text-ivory text-sm font-medium">{t('transcription.processing')}</p>
              </div>
              <p className="text-pearl/60 text-xs">{t('transcription.processingDescription')}</p>
            </motion.div>
          )}
          {transcriptionStatus === 'processing' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="glass-card rounded-xl p-4 text-center border border-gold/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Loader2 size={16} className="text-gold animate-spin" />
                <p className="text-ivory text-sm font-medium">{t('transcription.generating')}</p>
              </div>
              <p className="text-pearl/60 text-xs">{t('transcription.generatingDescription')}</p>
            </motion.div>
          )}
          {transcriptionStatus === 'done' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="glass-card rounded-xl p-4 text-center border border-emerald/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CheckCircle2 size={16} className="text-emerald" />
                <p className="text-ivory text-sm font-medium">{t('transcription.done')}</p>
              </div>
              <p className="text-pearl/60 text-xs">{t('transcription.doneDescription')}</p>
              <div className="flex items-center justify-center gap-2 mt-3">
                {notebookEntryId && onNavigateToNote && (
                  <button
                    onClick={() => {
                      const id = notebookEntryId
                      handleClose()
                      onNavigateToNote(id)
                    }}
                    className="px-4 py-2 rounded-xl bg-gold/20 border border-gold/30 text-gold text-sm font-medium hover:bg-gold/30 transition-colors"
                  >
                    <FileText size={14} className="inline mr-1.5 -mt-0.5" />
                    {t('transcription.viewNotes')}
                  </button>
                )}
                <button onClick={handleClose} className="px-4 py-2 rounded-xl bg-slate-mid/30 border border-slate-mid/50 text-ivory text-sm font-medium hover:bg-slate-mid/50 transition-colors">
                  {t('transcription.close')}
                </button>
              </div>
            </motion.div>
          )}
          {transcriptionStatus === 'failed' && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="glass-card rounded-xl p-4 text-center border border-rose/20">
              <div className="flex items-center justify-center gap-2 mb-1">
                <AlertCircle size={16} className="text-rose" />
                <p className="text-ivory text-sm font-medium">{t('transcription.failed')}</p>
              </div>
              <p className="text-pearl/60 text-xs">{t('transcription.failedDescription')}</p>
              <div className="flex items-center justify-center flex-wrap gap-2 mt-3">
                <button
                  onClick={handleRetry}
                  disabled={!recordingId || retrying}
                  className="px-4 py-2 rounded-xl bg-gold/20 border border-gold/30 text-gold text-sm font-medium hover:bg-gold/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  {retrying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  {retrying ? t('transcription.retrying') : t('transcription.retry')}
                </button>
                {notebookEntryId && onNavigateToNote && (
                  <button onClick={handleViewNotebookEntry} className="px-4 py-2 rounded-xl bg-slate-mid/30 border border-slate-mid/50 text-ivory text-sm font-medium hover:bg-slate-mid/50 transition-colors">
                    <FileText size={14} className="inline mr-1.5 -mt-0.5" />
                    {t('transcription.viewNotes')}
                  </button>
                )}
                <button onClick={handleClose} className="px-4 py-2 rounded-xl bg-transparent border border-slate-mid/40 text-pearl/70 text-sm font-medium hover:text-ivory hover:bg-slate-mid/20 transition-colors">
                  {t('transcription.close')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showDiscardConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 z-10 rounded-t-3xl flex items-center justify-center p-6"
            onClick={() => setShowDiscardConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-card rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="font-display text-ivory font-semibold text-lg mb-2">{t('discardConfirm.title')}</h3>
              <p className="text-pearl/50 text-sm mb-6">{t('discardConfirm.description')}</p>
              <div className="flex gap-3">
                <button onClick={() => setShowDiscardConfirm(false)} className="flex-1 py-2.5 rounded-xl bg-slate-mid/30 border border-slate-mid/50 text-ivory text-sm font-medium hover:bg-slate-mid/50 transition-colors">
                  {t('discardConfirm.cancel')}
                </button>
                <button onClick={handleDiscard} className="flex-1 py-2.5 rounded-xl bg-rose/20 border border-rose/30 text-rose text-sm font-medium hover:bg-rose/30 transition-colors">
                  {t('discardConfirm.confirm')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BottomSheet>
  )
}
